import { Application, Loader, Geometry, Shader, Mesh, RenderTexture, filters, Sprite } from 'pixi.js';
import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

export default class LsvpLogo3D {
    static SPRITE_SHEET_PATH = `${window.theme_assets_url}/images/lsvp-logo-3d/lsvp-logo-v3.png`
    static OVERLAY_PATH = `${window.theme_assets_url}/images/lsvp-logo-3d/overlay.png`

    constructor() {
        this.initApp();
        this.loadSprites();
        this.currentMouse = 1;
        this.targetMouse = .5;
        this.opacity = 0;
        this.scrollerPos = 0;
    }

    initApp() {
        this.containerEl = document.getElementById('lsvp-logo-3d');
        this.parent = document.getElementById('introduction');
        this.scroller = window.scroller;
        this.initialized = false;
        this.mouseInitialized = false;

        this.app = new Application({
            backgroundAlpha: 0,
            resolution: window.devicePixelRatio || 1,
            autoDensity: true,
            antialias: true,
            width: this.containerEl.offsetWidth,
            height: this.containerEl.offsetHeight
        });

        this.containerEl.appendChild(this.app.view);
    }

    initScrollTrigger() {
        ScrollTrigger.create({
            trigger: this.parent,
            start: 'top top',
            end: ()=> `+=${window.innerHeight / 2}`,
            scroller: '[data-scroll-container]',
            invalidateOnRefresh: true,
            scrub: true,
            onUpdate: self => {
                this.scrollerPos = self.progress * .25;
            }
        });
    }

    loadSprites() {
        Loader.shared
            .add('logo', LsvpLogo3D.SPRITE_SHEET_PATH)
            .add('overlay', LsvpLogo3D.OVERLAY_PATH)
            .load((loader, resources) => {
                this.setup(resources.logo.texture, resources.overlay.texture);
            })

    }

    setup(texture, overlayTexture) {
        const vertexShader = `
            attribute vec2 aVertexPosition;
            attribute vec2 aTextureCoord;

            uniform mat3 projectionMatrix;
            uniform mat3 translationMatrix;

            varying vec2 vTextureCoord;

            void main() {
                gl_Position = vec4((projectionMatrix * translationMatrix * vec3(aVertexPosition, 1.0)).xy, 0.0, 1.0);
                vTextureCoord = aTextureCoord;
            }`;

        const fragmentShader = `
            precision highp float;
            varying vec2 vTextureCoord;
            uniform sampler2D uSampler;
            uniform sampler2D uOverlay;
            uniform float uMouse;
            uniform float uOffsetX;
            uniform float uOffsetY;
            uniform float uScale;
            uniform float uOverlayStrength;
            uniform float uAlpha;

            vec2 getSubUV(vec2 uv, float index) {
                float ind = index / 8.0;
                vec2 uv1 = uv / 8.0;
                uv1.y += floor(ind) / 8.0;
                uv1.x += mod(floor(ind * 8.0), 8.0) / 8.0;
                return uv1;
            }

            vec3 overlayBlend(vec3 base, vec3 blend) {
                return mix(
                    2.0 * base * blend,
                    1.0 - 2.0 * (1.0 - base) * (1.0 - blend),
                    step(0.5, base)
                );
            }

            void main() {
                float index = mix(1.0, 63.0, uMouse);
                float blend = fract(index);
                vec2 subUV = getSubUV(vTextureCoord, index);
                vec2 subUV2 = getSubUV(vTextureCoord, index + 1.0);
                
                vec4 diffuse = texture2D(uSampler, subUV);
                vec4 diffuseNext = texture2D(uSampler, subUV2);
                
                vec4 spriteColor = mix(diffuse, diffuseNext, blend);
                
                vec2 overlayUV = (vTextureCoord - 0.5) / uScale + 0.5;
                overlayUV += vec2(uOffsetX, uOffsetY);
                
                vec4 overlayColor = texture2D(uOverlay, overlayUV);
                
                vec3 blendedColor = overlayBlend(spriteColor.rgb, overlayColor.rgb);
                
                float alpha = spriteColor.a * uAlpha;
                
                gl_FragColor = vec4(mix(spriteColor.rgb, blendedColor, overlayColor.a * uOverlayStrength), alpha);
            }`;

        const uniforms = {
            uSampler: texture,
            uOverlay: overlayTexture,
            uMouse: 0.0,
            uOffsetX: 0.3,
            uOffsetY: -0.07,
            uScale: 2.0,
            uOverlayStrength: 1.6,
            uAlpha: 0.0
        };

        const geometry = new Geometry()
            .addAttribute('aVertexPosition', [
                -1, -1,
                1, -1,
                1, 1,
                -1, 1
            ], 2)
            .addAttribute('aTextureCoord', [
                0, 0,
                1, 0,
                1, 1,
                0, 1
            ], 2)
            .addIndex([0, 1, 2, 0, 2, 3]);

        this.shader = Shader.from(vertexShader, fragmentShader, uniforms);

        this.quad = new Mesh(geometry, this.shader);

        const scale = Math.min(this.app.screen.width, this.app.screen.height) / 2;
        this.quad.scale.set(scale);

        this.quad.position.set(this.app.screen.width / 2, this.app.screen.height / 2);

        this.renderTexture = RenderTexture.create({
            width: this.app.screen.width,
            height: this.app.screen.height
        });

        this.outputSprite = new Sprite(this.renderTexture);

        this.blurFilter = new filters.BlurFilter(23, 12);
        this.outputSprite.filters = [this.blurFilter];

        this.app.stage.addChild(this.outputSprite);

        window.addEventListener('mousemove', this.onMouseMove.bind(this));

        this.app.ticker.add(this.animate.bind(this));

        this.initScrollTrigger();
    }

    onMouseMove(event) {
        if (!this.mouseInitialized) return;

        this.targetMouse = event.clientX / window.innerWidth  * .5 + .25;
    }

    easeOutPower(t, power) {
        return 1 - Math.pow(1 - t, power);
    }

    animate(delta) {
        const easing = 0.07;
        const diff = this.targetMouse - this.currentMouse;

        if(this.mouseInitialized) {
            if (Math.abs(diff) > 0.0001) {
                this.currentMouse += diff * this.easeOutPower(easing, 2);
            } else {
                this.currentMouse = this.targetMouse;
            }
        } else {
            if (Math.abs(diff) > 0.003) {
                this.currentMouse += diff * 0.04;
            } else {
                this.currentMouse = this.targetMouse;
                this.mouseInitialized = true;
            }
        }
        this.shader.uniforms.uMouse = this.currentMouse - this.scrollerPos;
        this.shader.uniforms.uAlpha = this.opacity;

        if(this.opacity < 0.999) {
            this.opacity += (1 - this.opacity) * 0.03;
        } else {
            this.opacity = 1;
        }

        this.app.renderer.render(this.quad, { renderTexture: this.renderTexture });
    }

    destroy() {
        window.removeEventListener('mousemove', this.onMouseMove);
        this.app.destroy();
    }
}
